import { ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { ESATabService } from '../udl-overlap-tool/esa-tab.service';
import { Project } from 'src/app/shared/models/project';
import { SelectedProjectApiService } from 'src/app/shared/components/selected-project/selected-project.api.service';
import { UdlOverlapToolContainerService } from 'src/app/esa-assessment/udl-overlap-tool/udl-overlap-tool-container.service';
import { EPAProjectRunService } from '../esa-output/project-status/esa-project-run.api.service';
import { EPAProjectInputsService } from '../esa-setup/esa-setup-inputs/esa-setup-inputs.api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-esa-run-project',
  templateUrl: './esa-run-project.component.html',
  styleUrls: ['./esa-run-project.component.css']
})
export class ESARunProjectComponent {
  loading: boolean = true;
  isInputsModelFilesValidationPassed: boolean = false;
  destroyRef = inject(DestroyRef)
  columnDefs: any;
  rowData: any[] | null | undefined;
  defaultColDef = { sortable: true, filter: true, resizable: true };
  isProjectToBeImported: boolean = false;

  constructor(
    private tabService: ESATabService,
    public selectedProjectApiService: SelectedProjectApiService,
    private udlOverlapToolContainerService: UdlOverlapToolContainerService,
    private cdr: ChangeDetectorRef,
    private projectRunApiService: EPAProjectRunService,
    private projectInputsService: EPAProjectInputsService
  ) { }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    if (this.projectRunApiService.isProjectRunning) {
      this.GoToOutput();
      this.loading = false;
      return;
    }

    this.isProjectToBeImported = this.udlOverlapToolContainerService.isProjectToBeImported();

    await this.projectInputsService.saveProjectIfUnsavedChangesExist(this.isProjectToBeImported); // checks if unsaved changes exist before calling save

    this.toggleSetupTab(false);
    this.toggleOutputTab(false);

    this.getColumnDef();

    if (this.udlOverlapToolContainerService.projectPk > 0) // project has been saved before
      this.getFilesValidationErrorList(this.udlOverlapToolContainerService.projectPk);
    else // project is new we need to wait for projectPk to be available
      this.udlOverlapToolContainerService.projectFilesUpdatedEvent.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (projectPk: number) => this.getFilesValidationErrorList(projectPk));
  }

  private getColumnDef(): void {
    this.columnDefs = [
      { headerName: 'File Name', field: 'fileName', width: 350 },
      { headerName: 'File Error', field: 'fileError', width: 900 },
      { headerName: 'Column', field: 'column', width: 150 },
      { headerName: 'Row', field: 'row', width: 150 },
      { headerName: 'Value', field: 'value', width: 250 }
    ];
  }

  getFilesValidationErrorList(projectPk: number): void {
    this.udlOverlapToolContainerService
      .getFilesValidationErrors(projectPk)
      .subscribe({
        next: (response) => {
          this.rowData = this.parseValidationErrors(response);
          this.loading = false;
        },
        error: (err) => {
          console.error('Error - fetching files validation errors:', err);
          this.rowData = [];
          this.loading = false;
        },
        complete: () => {
        }
    });

    this.toggleSetupTab(true);
  }

  private parseValidationErrors(response: any): any[] {
    if (!response?.Value) {
      return [];
    }

    if (response?.Value?.toString().trim().toLowerCase() === "epa model input files validated successfully".toLowerCase()) {
      this.isInputsModelFilesValidationPassed = true;
      this.toggleOutputTab(true);

      return [];
    }
    else {
      this.isInputsModelFilesValidationPassed = false;
    }

    const rows: any[] = [];
    response.Value.forEach((file: any) => {
      const fileName = file.fileName || 'Unknown File';
      const errorDetails = file.errorDetails || [];

      errorDetails.forEach((error: any) => {
        rows.push({
          fileName: fileName,
          fileError: error.errorMessage || 'Unknown Error',
          column: error.column || 'N/A',
          row: error.row || 'N/A',
          value: error.rawValue || 'N/A',
        });
      });
    });

    return rows;
  }

  setLoadingState(pState: boolean): void {
    this.loading = pState;
  }

  toggleSetupTab(enable: boolean): void {
    this.tabService.toggleTab(0, enable);
    this.cdr.detectChanges();
  }

  toggleOutputTab(enable: boolean): void {
    this.tabService.toggleTab(2, enable);
    this.cdr.detectChanges();
  }

  RunAndGoToOutput() {
    if (!this.projectRunApiService.isProjectRunning && this.isInputsModelFilesValidationPassed) {
      this.projectRunApiService.startRunIfAllowed();
    }
    this.GoToOutput();
  }

  GoToOutput() {
    const nextItem = this.tabService.items.find(i => i.index === 2);
    if (nextItem) {
      this.tabService.setActiveItem(nextItem);
    }
  }

  BackToSetup(){
    const nextItem = this.tabService.items.find(i => i.index == 0);
    if (nextItem) {
      this.tabService.setActiveItem(nextItem);
    }
  }
}
